import { useStoreState } from '@src/model/hooks';
import { pushToDataLayer } from '@src/services/gtmService';
import { getBasicCommentEventData } from '@src/store/comments/helpers/analytics';
import type { ContentEntityType } from '@src/store/contentEntities/model';
import { getBasicPostEventData } from '@src/store/posts/helpers/analytics';
import { postReaction } from '@src/store/reactions/reactions';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface UseReactionPayload {
    entityId: string;
    entityType: ContentEntityType;
}

export interface UseReaction {
    isReacting: boolean;
    react: (reactionName: string) => Promise<void>;
}

function useReaction(payload: UseReactionPayload): UseReaction {
    const { entityId, entityType } = payload;
    const { t } = useTranslation();

    const [isReacting, setIsReacting] = useState(false);
    const myUserId = useStoreState(state => state.user.myUserId);

    function mapError(error) {
        if (error.message.includes('banned')) return alert(t('errors.bannedFromCommunity'));
    }

    function trackEvent(reactionName: string) {
        const eventData = {
            event: 'reaction',
            reactionName,
            entityType: entityType,
            entityId: entityId,
        };
        if (entityType === 'Post') Object.assign(eventData, getBasicPostEventData(entityId));
        else Object.assign(eventData, getBasicCommentEventData(entityId));

        pushToDataLayer(eventData);
    }

    async function react(reactionName: string) {
        setIsReacting(true);
        try {
            await postReaction({
                userId: myUserId,
                entityId,
                entityType,
                reactionName,
            });
            trackEvent(reactionName);
        } catch (error) {
            mapError(error);
            console.error(error);
        } finally {
            setIsReacting(false);
        }
    }

    return { isReacting, react };
}

export default useReaction;
